import type {
  IStudentDetailState,
  ISchoolStudentDetailApiResponse,
} from '@/types/class';

import React, { useState, useEffect } from 'react';

import { IconStar, IconMoodKid, IconCalendarEvent } from '@tabler/icons-react';

import { UserAvatarList } from '@/components/Avatar';
import CustomImage from '@/components/CustomImage';
import CustomModal from '@/components/CustomModal';
import ProgressBar from '@/components/ProgressBar';
import StudentDetailDrawerSkeleton from '@/components/SkeletonLoader/StudentDetailDrawerSkeleton';
import { useFormatText } from '@/hooks/useFormatText';
import { getStudentDetail } from '@/service/studentService';
import { useSkeletonStore } from '@/store/useSkeletonStore';
import { getAssetUrl, formatUnixToLongDate } from '@/utils';

// Minimal student shape — works with IClassDetailsStudent, IStudent, or any object with id
interface IStudentDrawerStudent {
  id: string;
  progress?: number;
}

interface IStudentDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  student: IStudentDrawerStudent | null;
}

export const StudentDetailDrawer: React.FC<IStudentDetailDrawerProps> = ({
  isOpen,
  onClose,
  student,
}) => {
  const { formatText } = useFormatText();
  const { isDrawerLoading, setDrawerLoading } = useSkeletonStore();
  const [studentDetail, setStudentDetail] =
    useState<IStudentDetailState | null>(null);

  useEffect(() => {
    const fetchStudentDetail = async () => {
      if (!isOpen || !student?.id) return;

      setDrawerLoading(true);
      try {
        const res = await getStudentDetail(student.id);
        if (res?.status && res?.data) {
          const detail = res.data as ISchoolStudentDetailApiResponse;

          const ageYearsText = detail.age ? ` (${detail.age} years)` : '';
          setStudentDetail({
            id: detail.id,
            full_name: detail.full_name,
            profile_pic: getAssetUrl(detail?.profile_pic),
            class_name: detail.class_name || '-',
            age: detail.age ? String(detail.age) : '',
            gender: detail.gender || '-',
            date_of_birth: detail.date_of_birth
              ? `${formatUnixToLongDate(detail.date_of_birth)}${ageYearsText}`
              : '-',
            device_name: detail?.device_name || '-',
            current_progress: detail.learning_overview?.current_progress ?? 0,
            stars_earned: detail.learning_overview?.stars_earned ?? 0,
            learning_modules: Array.isArray(detail.learning_modules)
              ? detail.learning_modules.map((module, index) => ({
                  id: module.lesson_id || String(index),
                  name: module.lesson_name || '-',
                  games: Number(module.games_count || 0),
                  progress: Number(module.progress || 0),
                }))
              : [],
          });
        } else {
          setStudentDetail(null);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setDrawerLoading(false);
      }
    };

    fetchStudentDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, student]);

  if (!isOpen || !student) return null;

  return (
    <CustomModal
      setIsOpen={onClose}
      heading={formatText('studentDetail.title')}
      isHeader
      className="max-w-full"
      position="right"
      childClassName="p-6 space-y-10"
      dataTestId="student-detail-drawer"
    >
      {isDrawerLoading || !studentDetail ? (
        <div className="w-full" data-testid="student-drawer-skeleton">
          <StudentDetailDrawerSkeleton />
        </div>
      ) : (
        <div data-testid="student-detail-content" className="w-full">
          <div className="flex flex-col items-center gap-4 w-full">
            <UserAvatarList
              user={studentDetail.full_name}
              image={studentDetail?.profile_pic || ''}
              className="w-25 h-25 text-3xl!"
              dataTestId="student-avatar"
            />

            <h2
              className="text-2xl font-bold text-neutral-primary text-center capitalize"
              data-testid="student-name"
            >
              {studentDetail.full_name}
            </h2>

            <div
              className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-50"
              data-testid="student-class"
            >
              <span className="text-xs font-semibold text-success-secondary">
                {studentDetail.class_name}
              </span>
            </div>

            <div className="flex items-center justify-center gap-8.75 w-full text-sm">
              <div
                className="flex items-center gap-1"
                data-testid="student-age"
              >
                <span className="text-neutral-secondary text-nowrap">
                  {formatText('studentDetail.age')}:
                </span>
                <span className="text-neutral-primary text-nowrap">
                  {studentDetail.age
                    ? `${studentDetail.age} ${formatText('studentDetail.years')}`
                    : '-'}
                </span>
              </div>
              <div
                className="flex items-center gap-1"
                data-testid="student-gender"
              >
                <span className="text-neutral-secondary text-nowrap">
                  {formatText('studentDetail.gender')}:
                </span>
                <span className="text-neutral-primary text-nowrap capitalize">
                  {studentDetail.gender}
                </span>
              </div>
              <div
                className="flex items-center gap-1"
                data-testid="student-device"
              >
                <span className="text-neutral-secondary text-nowrap">
                  {formatText('studentDetail.device') || 'Device'}:
                </span>
                <span className="text-neutral-primary text-nowrap">
                  {studentDetail.device_name}
                </span>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 w-full mt-10">
            <div
              className="flex items-start gap-2 flex-1"
              data-testid="student-full-name"
            >
              <IconMoodKid size={24} className="text-neutral-secondary mt-1" />
              <div className="flex flex-col gap-1">
                <p className="text-sm text-neutral-secondary">
                  {formatText('studentDetail.fullName')}
                </p>
                <p className="text-base text-neutral-primary max-w-60 capitalize">
                  {studentDetail.full_name}
                </p>
              </div>
            </div>

            <div
              className="flex items-start gap-2 flex-1"
              data-testid="student-dob"
            >
              <IconCalendarEvent
                size={24}
                className="text-neutral-secondary mt-1"
              />
              <div className="flex flex-col gap-1">
                <p className="text-sm text-neutral-secondary">
                  {formatText('studentDetail.dateOfBirth')}
                </p>
                <p className="text-base text-neutral-primary">
                  {studentDetail.date_of_birth}
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 w-full mt-10">
            <h3 className="text-lg font-semibold text-neutral-primary">
              {formatText('studentDetail.learningOverview.title')}
            </h3>

            <div
              className="border border-neutral-200 rounded-2xl p-4 space-y-6"
              data-testid="learning-overview-card"
            >
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between text-base">
                  <span className="text-neutral-secondary">
                    {formatText(
                      'studentDetail.learningOverview.currentProgress'
                    )}
                  </span>
                  <span
                    className="font-semibold text-neutral-primary"
                    data-testid="student-progress-value"
                  >
                    {studentDetail.current_progress}%
                  </span>
                </div>
                <ProgressBar
                  value={studentDetail.current_progress}
                  showPercentage={false}
                  height="h-2"
                  dataTestId="student-progress-bar"
                />
              </div>

              <div className="flex gap-6">
                <div
                  className="flex-1 bg-yellow-100 rounded-lg px-4 py-2 flex flex-col items-center justify-center h-18"
                  data-testid="stars-earned"
                >
                  <p className="text-xs font-semibold text-warning-secondary">
                    {formatText('studentDetail.learningOverview.starsEarned')}
                  </p>
                  <div className="flex items-center gap-1.5">
                    <IconStar
                      size={24}
                      className="text-yellow-500 fill-yellow-500"
                    />
                    <span className="text-2xl font-semibold text-warning-primary">
                      {studentDetail.stars_earned}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-4 w-full mt-10">
            <h3 className="text-lg font-semibold text-neutral-primary">
              {formatText('studentDetail.learningModules.title')}
            </h3>

            <div className="space-y-4" data-testid="learning-modules-list">
              {studentDetail?.learning_modules?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 gap-3">
                  <CustomImage
                    src={'/assets/images/lesson-breakdown-empty.png'}
                    alt="noData"
                    width={100}
                    height={100}
                  />
                  <p className="text-neutral-900 font-semibold">
                    {formatText('common.no-data-found')}
                  </p>
                </div>
              ) : (
                studentDetail?.learning_modules?.map((lesson) => (
                  <div
                    key={lesson.id}
                    data-testid={`learning-module-${lesson.id}`}
                    className="border border-neutral-200 rounded-2xl p-4"
                  >
                    <div className="flex flex-col gap-2">
                      <div className="flex items-end justify-between">
                        <div className="flex flex-col gap-1">
                          <p className="text-base font-semibold text-neutral-primary">
                            {lesson.name}
                          </p>
                          <p className="text-base text-neutral-secondary">
                            {lesson.games}{' '}
                            {formatText('studentDetail.learningModules.games')}
                          </p>
                        </div>
                        <span className="text-lg font-semibold text-neutral-primary">
                          {lesson.progress}%
                        </span>
                      </div>
                      <ProgressBar
                        value={lesson.progress}
                        showPercentage={false}
                        height="h-2"
                        dataTestId={`module-progress-${lesson.id}`}
                      />
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </CustomModal>
  );
};
